import { cn } from '@spiaggeit/spit-ui'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppSelector } from '@/hooks/store'
import { Service } from '@/models/service'

import { cartSlice } from '@/store/cartSlice'
import { SERVICE_LABEL_LIMIT } from '@/utils/constants'
import { getBoughtServicesTotal } from '@/utils/timeSchedules'
import { Minus } from '../../assets/icons/Minus'
import { Plus } from '../../assets/icons/Plus'
import { priceFormatter } from '../../utils/price'
import { IconButton } from '../IconButton'
import { TimeScheduleMapTrigger } from '../TimeScheduleDialog/TimeScheduleMapTrigger'
import { Tooltip } from '../Tooltip/Tooltip'

interface Props {
  available: {
    global?: number
    group?: number
    ticket: number
  }
  hasLimit: boolean
  icon: string
  name: string
  minLabel?: string
  maxLabel?: string
  minimumQuantity: number
  price?: number
  initialQuantity?: number
  showSoldOutLabel?: boolean
  service: Service
  onAdd: VoidFunction
  onRemove: VoidFunction
  onAddDisabled?: VoidFunction
}

export function ServiceQuantityController(props: Props) {
  const { t } = useTranslation()

  const selectedElementQuotableProducts = useAppSelector(
    cartSlice.selectors.selectedElementQuotableProducts
  )

  const quantity = useMemo(() => {
    const bought =
      selectedElementQuotableProducts.boughtServices[props.service.serviceId]
    return getBoughtServicesTotal(bought)
  }, [selectedElementQuotableProducts, props.service.serviceId])

  const global = props.available?.global ?? Infinity
  const group = props.available?.group ?? Infinity
  const ticket = props.available?.ticket ?? Infinity

  const max = props.hasLimit ? Math.min(global, group, ticket) : Infinity
  const availableQuantity = props.hasLimit
    ? Math.min(global, group, ticket - quantity)
    : Infinity

  const isSoldOut =
    props.service.scheduleType === 'daily' &&
    props.hasLimit &&
    props?.available.ticket === 0

  useEffect(() => {
    if (quantity >= 0 && quantity < props.minimumQuantity) {
      props.onAdd()
    }
  }, [props.minimumQuantity])

  useEffect(() => {
    if (quantity > 0 && quantity > props.available.ticket) {
      props.onRemove()
    }
  }, [max, quantity])

  function handleRemove() {
    if (quantity > props.minimumQuantity) {
      props.onRemove()
    }
  }

  function handleAdd() {
    if (availableQuantity > 0) {
      props.onAdd()
    }
  }

  const isAddDisabled = availableQuantity < 1
  const isRemoveDisabled = quantity === 0 || quantity <= props.minimumQuantity

  const limitLabel = ((): string => {
    const minLabel =
      props.minLabel || t('listItem.min', { value: props.minimumQuantity })

    const maxLabel =
      props.service.scheduleType === 'daily' &&
      availableQuantity < SERVICE_LABEL_LIMIT
        ? props.maxLabel || t('listItem.max', { value: props.available.ticket })
        : ''

    const showMaxLabel = props.hasLimit && !!maxLabel

    const labels: string[] = []

    if (props.minimumQuantity > 0) {
      labels.push(minLabel)
    }

    if (showMaxLabel) {
      labels.push(maxLabel)
    }

    return labels.join(' · ')
  })()

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 first:rounded-t last:rounded-b',
        {
          'cursor-not-allowed opacity-50': isSoldOut,
        }
      )}
    >
      <div className="flex flex-auto items-center gap-3">
        <div className="flex-initial shrink-0">
          <img
            alt={props.name}
            className="size-8"
            src={`/icons/${props.icon}`}
          />
        </div>

        <div className="flex-auto">
          <Tooltip
            className="font-medium"
            content={props.name}
            maxLines={3}
            overflowStyle="underline"
          />

          <p className="text-sm leading-5">
            {props.price && props.price > 0 && (
              <span>{priceFormatter.format(props.price)} </span>
            )}

            {props.showSoldOutLabel ? (
              <span className="text-secondary">{t('listItem.soldOut')}</span>
            ) : null}
            <span className="text-secondary">{limitLabel}</span>
          </p>
        </div>
      </div>

      {props.service.scheduleType === 'custom' ||
      props.service.scheduleType === 'shifted' ? (
        <TimeScheduleMapTrigger quantity={quantity} service={props.service} />
      ) : (
        <div className="flex-0 relative flex items-center">
          <IconButton
            ariaLabel={t('itemQuantityController.remove')}
            icon={Minus}
            isDisabled={isRemoveDisabled}
            onClick={handleRemove}
          />

          <div className="flex size-10 items-center justify-center text-sm font-bold">
            {quantity}
          </div>

          <IconButton
            ariaLabel={t('itemQuantityController.add')}
            icon={Plus}
            isDisabled={isAddDisabled}
            onClick={handleAdd}
          />

          {props.onAddDisabled && isAddDisabled && (
            <button
              className="absolute right-0 top-0 size-10"
              onClick={props.onAddDisabled}
            />
          )}
        </div>
      )}
    </div>
  )
}
